<template>
  <div id="a-pricing-strategy-strategy-detail">
    <a-base-component
      :subscribed_modules="subscribed_modules"
      @loaded="measureBodyTop"
    >
      <template v-slot:default>
        <!-- Flex column con min-height (no height): si el contenido no llega
             al alto del viewport, el espaciador crece y baja los guardarailes
             al fondo; si lo pasa, el espaciador colapsa y todo fluye seguido
             con el scroll de la ventana. -->
        <div
          ref="body"
          class="d-flex flex-column"
          :style="guardrailsSupported ? { minHeight: bodyMinHeight } : null"
        >
          <div class="d-flex align-center justify-space-between mt-6">
            <div class="d-flex align-center" style="gap: 10px;">
              <v-btn
                color="grey"
                icon
                small
                @click="$router.push({ name: 'pricing_strategy_strategies' })"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-avatar size="20" :color="strategy.color"></v-avatar>
              <div>
                <div class="text-h4 font-weight-bold">
                  {{ strategy.name }}
                </div>
              </div>
              <div
                v-if="ratePlanCode"
                class="d-flex align-center grey--text mt-1"
                style="gap: 5px;"
              >
                <span>|</span>
                <span style="margin-top: 3px">{{ $t("_rate") }}</span>
                <span
                  class="text-uppercase font-weight-bold"
                  style="margin-top: 3px"
                >
                  {{ ratePlanCode }}
                </span>
              </div>
              <v-btn icon color="primary" @click="showEditStrategy()">
                <v-icon size="20">mdi-pencil-outline</v-icon>
              </v-btn>
            </div>
          </div>

          <div class="text-body-2 mt-2 mb-6">
            {{ strategy.description }}
          </div>

          <!-- Rules grid -->
          <a-pricing-strategy-grid-rules
            v-if="anticipation_slots.length > 0"
            :anticipation-slots="anticipation_slots"
            :strategy-rules-data="strategy"
            class="mb-6"
            @update="handleRuleUpdate"
          />

          <!-- Guardarailes: se evalúan después de la grid, así que van debajo
               para que el orden de lectura sea el de ejecución -->
          <template v-if="guardrailsSupported">
            <!-- Se come el hueco sobrante y ancla el bloque abajo -->
            <div class="flex-grow-1"></div>

            <div class="guardrails-divider d-flex align-center mb-4">
              <v-divider />
              <span
                class="text-caption grey--text text-uppercase font-weight-medium px-3"
              >
                {{ $t("after_price_calculation") }}
              </span>
              <v-divider />
            </div>

            <a-pricing-strategy-guardrails
              :strategy="strategy"
              class="mb-6"
              @update="handleGuardrailUpdate"
            />
          </template>
        </div>

        <!-- Edit strategy -->
        <a-pricing-strategy-set-strategy-dialog />

        <!-- Rule templates dialog -->
        <a-pricing-strategy-rule-templates-dialog />

        <!-- Rule definition dialog (canvas casi-fullscreen) -->
        <v-dialog
          v-model="rule_definition_dialog"
          :fullscreen="$vuetify.breakpoint.smAndDown"
          :retain-focus="false"
          content-class="canvas-rule-dialog"
        >
          <a-pricing-strategy-rule-canvas
            v-if="rule_definition_dialog"
            :mode="rule_definition_mode"
            :max-routes="isGuardrailMode ? guardrailMaxRoutes : ruleMaxRoutes"
            :max-conditions="
              isGuardrailMode ? guardrailMaxConditions : ruleMaxConditions
            "
          />
        </v-dialog>
      </template>
      <template v-slot:loading>
        <v-skeleton-loader type="table"> </v-skeleton-loader>
      </template>
    </a-base-component>
  </div>
</template>

<script>
import aBaseComponent from "@/components/aBaseComponent";
import aPricingStrategyGridRules from "@/components/pricing-strategy/aPricingStrategyGridRules";
import aPricingStrategyGuardrails from "@/components/pricing-strategy/aPricingStrategyGuardrails.vue";
import aPricingStrategyRuleCanvas from "@/components/pricing-strategy/aPricingStrategyRuleCanvas.vue";
import aPricingStrategySetStrategyDialog from "@/components/pricing-strategy/aPricingStrategySetStrategyDialog";
import aPricingStrategyRuleTemplatesDialog from "@/components/pricing-strategy/aPricingStrategyRuleTemplatesDialog";
import * as Utils from "@/utils.js";
import { mapGetters, mapState } from "vuex";

export default {
  name: "a-pricing-strategy-strategy-detail",
  components: {
    aBaseComponent,
    aPricingStrategyGridRules,
    aPricingStrategyGuardrails,
    aPricingStrategyRuleCanvas,
    aPricingStrategySetStrategyDialog,
    aPricingStrategyRuleTemplatesDialog
  },
  data: () => ({
    subscribed_modules: [
      // Los datasets de guardarailes NO van aquí: subscribed_modules gatea el
      // render de toda la pantalla, y sus endpoints puede que aún no existan.
      // Los pide aPricingStrategyGuardrails, que solo se monta si el backend
      // declara soporte.
      "pricingstrategy_rules",
      "pricingstrategy_rule_types",
      "pricingstrategy_rule_templates",
      "pricingstrategy_strategy_config",
      "pricingstrategy_anticipation_slots",
      "pricingstrategy_strategy",
      "pricingstrategy_strategy_rates",
      "pricingstrategy_strategy_room_types"
    ],
    // Distancia del bloque al inicio del documento. Se mide en vez de
    // calcularse: encima hay márgenes negativos (aPage y la barra de la grid)
    // que harían frágil cualquier número mágico en el calc().
    bodyTop: 0
  }),
  computed: {
    strategy_id: {
      get() {
        return this.$store.state.pricingstrategy.strategy_id;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyStrategyId", value);
      }
    },
    strategy: {
      get() {
        return this.$store.state.pricingstrategy.strategy;
      },
      set(value) {
        this.$store.commit("SET_STRATEGY", value);
      }
    },
    rule_definition_dialog: {
      get() {
        return this.$store.state.pricingstrategy.rule_definition_dialog;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyRuleDefinitionDialog", value);
      }
    },
    rule_definition_mode() {
      return this.$store.state.pricingstrategy.rule_definition_mode;
    },
    isGuardrailMode() {
      return this.rule_definition_mode === "guardrail";
    },
    // Ni guardarailes ni reglas tienen tope de rutas ni de condiciones: las dos
    // pueden ramificar y encadenar condiciones con AND. El backend puede
    // imponer uno con max_routes / max_conditions si algún día hace falta; sin
    // ellos, ilimitado.
    guardrailMaxRoutes() {
      const config = this.$store.state.pricingstrategy.strategy_config;
      const max = config && config.guardrail && config.guardrail.max_routes;
      return max || Infinity;
    },
    ruleMaxRoutes() {
      const config = this.$store.state.pricingstrategy.strategy_config;
      const max = config && config.rule && config.rule.max_routes;
      return max || Infinity;
    },
    guardrailMaxConditions() {
      const config = this.$store.state.pricingstrategy.strategy_config;
      const max = config && config.guardrail && config.guardrail.max_conditions;
      return max || Infinity;
    },
    ruleMaxConditions() {
      const config = this.$store.state.pricingstrategy.strategy_config;
      const max = config && config.rule && config.rule.max_conditions;
      return max || Infinity;
    },
    // La sección `guardrail` de /get-strategy-config es lo que declara el
    // soporte. El guardarail fijo de min/máx lo emite el backend dentro de
    // strategyGuardrails, así que sin backend no hay nada que enseñar.
    guardrailsSupported() {
      const config = this.$store.state.pricingstrategy.strategy_config;
      if (config && config.guardrail) return true;
      const existing =
        (this.strategy && this.strategy.strategyGuardrails) || [];
      return existing.length > 0;
    },
    strategy_to_edit: {
      get() {
        return this.$store.state.pricingstrategy.strategy_to_edit;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyStrategyToEdit", value);
      }
    },
    set_strategy_dialog: {
      get() {
        return this.$store.state.pricingstrategy.set_strategy_dialog;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategySetStrategyDialog", value);
      }
    },
    bodyMinHeight() {
      return `calc(100vh - ${this.bodyTop}px)`;
    },
    // Código de la tarifa de la estrategia; vacío si no se encuentra
    // (p.ej. la estrategia mockeada referencia un rate_plan_id que no
    // existe en las strategy_rates reales del hotel)
    ratePlanCode() {
      if (!this.strategy || !this.strategy.rate_plan_id) return "";
      const ratePlan = this.getPricingStrategyStrategyRatePlanById(
        this.strategy.rate_plan_id
      );
      return (ratePlan && ratePlan.code) || "";
    },
    ...mapState({
      anticipation_slots: state => state.pricingstrategy.anticipation_slots
    }),
    ...mapGetters({
      current_hotel: "current_hotel",
      getPricingStrategyStrategyRatePlanById:
        "getPricingStrategyStrategyRatePlanById"
    })
  },
  methods: {
    // Alto disponible = viewport menos todo lo que queda por encima del
    // bloque. Se mide sobre el documento, no sobre el viewport, para que el
    // valor no cambie al hacer scroll.
    //
    // Se llama desde `mounted` (datos ya en el store) y desde el @loaded de
    // aBaseComponent (datos que llegan después). Hace falta el segundo porque
    // `v-slot` compila como scoped slot: al pasar de cargando a cargado
    // re-renderiza aBaseComponent, no este componente, así que `updated` aquí
    // no se dispara y el bloque todavía no estaría en el DOM.
    measureBodyTop() {
      this.$nextTick(() => {
        const el = this.$refs.body;
        if (!el) return;
        const top = Math.round(
          el.getBoundingClientRect().top + window.pageYOffset
        );
        if (top !== this.bodyTop) this.bodyTop = top;
      });
    },
    showEditStrategy() {
      this.set_strategy_dialog = true;
      this.strategy_to_edit = JSON.parse(JSON.stringify(this.strategy));
    },
    handleRuleUpdate(data) {
      if (data.delete) {
        // console.log("🗑️ Regla eliminada:", data);
        const params = {
          strategy_id: data.strategy_id,
          strategy_rule_id: data.strategy_rule_id
        };
        this.$store
          .dispatch("deletePricingStrategyStrategyRule", params)
          .then(response => {
            this.strategy = response.data;
          })
          .catch(() => {
            Utils.showError(this, "rule_not_deleted");
          });
      } else {
        // console.log("🔄 Regla actualizada:", data);
        const params = {
          strategy_id: data.strategy_id,
          strategy_rule_id: data.strategy_rule_id,
          hotel_id: this.current_hotel.id,
          anticipation_slot_id: data.anticipation_slot_id,
          row_id: data.row_id,
          length: data.length,
          rule_id: data.rule_id
        };
        this.$store
          .dispatch("setPricingStrategyStrategyRule", params)
          .then(response => {
            this.strategy = response.data;
          })
          .catch(() => {
            Utils.showError(this, "rule_not_updated");
          });
      }
    },
    handleGuardrailUpdate(data) {
      let action;
      let params;
      let errorKey;
      let doneKey;

      if (data.delete) {
        action = "deletePricingStrategyStrategyGuardrail";
        params = {
          strategy_id: data.strategy_id,
          strategy_guardrail_id: data.strategy_guardrail_id
        };
        errorKey = "guardrail_not_deleted";
        doneKey = "guardrail_deleted";
      } else if (data.reorder) {
        action = "sortPricingStrategyStrategyGuardrails";
        params = {
          strategy_id: data.strategy_id,
          guardrail_ids: data.guardrail_ids
        };
        errorKey = "guardrail_not_updated";
        doneKey = "guardrail_updated";
      } else {
        action = "setPricingStrategyStrategyGuardrail";
        params = {
          strategy_id: data.strategy_id,
          strategy_guardrail_id: data.strategy_guardrail_id,
          hotel_id: this.current_hotel.id,
          rule_id: data.rule_id,
          position: data.position
        };
        errorKey = "guardrail_not_updated";
        doneKey = "guardrail_updated";
      }

      this.$store
        .dispatch(action, params)
        .then(response => {
          this.strategy = response.data;
          // Sin confirmación, el aviso de la operación ANTERIOR se queda en
          // pantalla y se lee como si fuera el de ésta: borrar un guardarail
          // acababa diciendo "Guardarail añadido".
          this.$store.dispatch("setSnackbar", {
            title: this.$t(doneKey),
            color: "accent"
          });
        })
        .catch(() => {
          Utils.showError(this, errorKey);
        });
    }
  },
  mounted() {
    this.strategy_id = this.$route.params.strategy_id;
    window.addEventListener("resize", this.measureBodyTop);
    this.measureBodyTop();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.measureBodyTop);
  }
};
</script>

<style lang="scss">
.canvas-rule-dialog {
  height: 98vh !important;
  width: 98vw !important;
  margin: auto !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px !important;

  .a-pricing-strategy-rule-canvas,
  .rule-canvas-root {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
  }
}
</style>
